import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Receipt, Printer, DownloadSimple, ArrowLeft } from "@phosphor-icons/react";

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_TONE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  succeeded: "bg-emerald-50 text-emerald-700 border-emerald-200",
  refunded: "bg-sky-50 text-sky-700 border-sky-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function Invoices() {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/invoices");
      setItems(data || []);
    } catch {
      setItems([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="invoices-title">Invoices</h1>
        <p className="text-slate-600 mt-1">Every paid invoice for your tax preparation. View or download for your records.</p>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border tf-hairline bg-white p-10 text-center">
          <Receipt size={40} weight="duotone" className="text-slate-400 mx-auto" />
          <div className="mt-3 font-[Chivo] font-bold text-[#0A2540]">No invoices yet</div>
          <p className="mt-1 text-sm text-slate-500">Invoices appear here once your CPA marks a preparation fee as paid.</p>
        </div>
      ) : (
        <div className="rounded-md border tf-hairline bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left p-3">Invoice #</th>
                <th className="text-left p-3">Purpose</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-left p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(inv => {
                const tone = STATUS_TONE[inv.status] || "bg-slate-100 text-slate-700 border-slate-200";
                return (
                  <tr key={inv.id} className="border-t tf-hairline hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                    <td className="p-3 font-mono text-xs">#{inv.id.slice(0, 8).toUpperCase()}</td>
                    <td className="p-3">{inv.purpose}</td>
                    <td className="p-3 font-mono text-xs">{(inv.updated_at || inv.created_at || "").split("T")[0]}</td>
                    <td className="p-3 text-right font-mono tabular-nums text-[#0A2540]">${money(inv.amount)}</td>
                    <td className="p-3">
                      <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${tone}`}>{inv.status}</span>
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => window.open(`/dashboard/invoices/${inv.id}/print`, "_blank", "noopener")} data-testid={`invoice-view-${inv.id}`}>
                        <Receipt size={14} className="mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function InvoicePrint() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${invoiceId}`);
        setInv(data);
        setTimeout(() => window.print(), 400);
      } catch {
        setErr("Invoice not found.");
      }
    })();
  }, [invoiceId]);

  if (err) return <div className="min-h-screen grid place-items-center bg-slate-50 p-8">
    <div className="rounded-md border tf-hairline bg-white p-8 text-center max-w-md">
      <div className="text-lg font-[Chivo] font-black text-[#D32F2F]">{err}</div>
      <Button className="mt-4" onClick={() => navigate("/dashboard/invoices")}>Back to invoices</Button>
    </div></div>;

  if (!inv) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  const invNum = `#${inv.id.slice(0, 8).toUpperCase()}`;
  const dateStr = (inv.updated_at || inv.created_at || "").split("T")[0];

  return (
    <div className="min-h-screen bg-slate-100 py-10 print:py-0 print:bg-white return-print-page">
      <div className="no-print max-w-3xl mx-auto mb-6 flex items-center justify-between px-6">
        <div className="text-slate-600 text-sm">Invoice preview — print or save as PDF.</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}><ArrowLeft size={14} className="mr-1" /> Back</Button>
          <Button size="sm" className="bg-[#0A2540]" onClick={() => window.print()}><Printer size={14} className="mr-1" /> Print / Save PDF</Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto bg-white shadow-sm print:shadow-none p-12 print:p-8 return-print-doc">
        <div className="flex items-start justify-between border-b-2 border-black pb-4">
          <div>
            <div className="text-2xl font-black uppercase tracking-widest text-[#0A2540]">Taxfree Filer LLP</div>
            <div className="text-xs text-slate-600 mt-1">taxfreefiler.com · info@taxfreefiler.com · +91 (738) 672-4363</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500">Invoice</div>
            <div className="text-2xl font-black text-[#0A2540]">{invNum}</div>
            <div className="text-xs text-slate-500 mt-1">Issued {dateStr}</div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-8 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Bill To</div>
            <div className="mt-1 font-bold text-[#0A2540]">{inv.owner_name || "—"}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Status</div>
            <div className={`mt-1 inline-block font-mono uppercase tracking-widest text-xs px-2 py-0.5 rounded border ${STATUS_TONE[inv.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{inv.status}</div>
          </div>
        </div>

        <div className="mt-10">
          <div className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-300 pb-1 mb-3">Details</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="py-2">Description</th>
                <th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="py-3">
                  <div className="text-[#0A2540]">{inv.purpose}</div>
                  {inv.return_type && <div className="text-xs text-slate-500 mt-0.5">Return: {inv.return_type} · TY {inv.tax_year}</div>}
                </td>
                <td className="py-3 text-right font-mono tabular-nums text-[#0A2540]">${money(inv.amount)}</td>
              </tr>
              <tr>
                <td className="pt-4 font-bold text-[#0A2540]">Total</td>
                <td className="pt-4 text-right font-mono tabular-nums text-xl font-black text-[#0A2540]">${money(inv.amount)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-16 text-xs text-slate-600 leading-relaxed">
          Thank you for choosing Taxfree Filer LLP. Payment received via {inv.method || "invoice"}. For questions about this invoice, reply to info@taxfreefiler.com.
        </div>

        <div className="mt-12 pt-4 border-t-2 border-black flex items-center justify-between text-[10px] text-slate-500">
          <span>Taxfree Filer LLP · CPA-supervised · Confidential</span>
          <span>Printed {new Date().toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}
